// src/auth/InstitutionAdminForgotPassword.js

import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    useTheme,
    useMediaQuery,
    Fade,
    Zoom,
    alpha,
    CircularProgress,
    Alert,
    InputAdornment
} from '@mui/material';
import {
    Person as PersonIcon,
    ArrowBack,
    Send,
    CheckCircle
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import useCustomToast from '../hooks/useToast';
import { motion } from 'framer-motion';
import axios from 'axios';
import { APIInstitutionAdminForgotPassword, APIInstitutionAdminResendVerification } from '../apiServices/APIs';

const StyledPaper = styled(Paper)(({ theme }) => ({
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 450,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(12px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
}));

const InstitutionAdminForgotPassword = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showToast = useCustomToast();
    
    const [identifier, setIdentifier] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [needsVerification, setNeedsVerification] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Simple non-empty validation (no strict regex)
        if (!identifier.trim()) {
            setError('Please enter your email, username, or phone number');
            return;
        }
        
        setLoading(true);
        setError('');
        setNeedsVerification(false);
        
        try {
            const response = await axios.post(APIInstitutionAdminForgotPassword, { email: identifier.trim() });
            
            if (response.data.status === 'success') {
                setSubmitted(true);
                showToast('Success', response.data.message, 'success');
            } else {
                setError(response.data.message || 'Failed to send reset link');
            }
        } catch (error) {
            if (error.response?.data?.data?.needs_verification) {
                setNeedsVerification(true);
                setError('Please verify your email address first');
            } else {
                setError(error.response?.data?.message || 'An error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setLoading(true);
        try {
            const response = await axios.post(APIInstitutionAdminResendVerification, { email: identifier.trim() });
            if (response.data.status === 'success') {
                showToast('Success', 'Verification email sent! Please check your inbox.', 'success');
                setNeedsVerification(false);
            }
        } catch (error) {
            showToast('Error', error.response?.data?.message || 'Failed to resend verification', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
            p: isMobile ? 2 : 3,
        }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0,
                    background: `${theme.palette.common.auth}`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.3) 100%)',
                    }
                }}
            />

            <Fade in={true} timeout={800}>
                <StyledPaper elevation={0}>
                    <Zoom in={true} timeout={500}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Typography
                                    variant={isMobile ? 'h5' : 'h4'}
                                    sx={{
                                        fontWeight: 'bold',
                                        mb: 1,
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }}
                                >
                                    Forgot Password?
                                </Typography>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Enter your email, username, or phone number to receive a password reset link
                                </Typography>
                            </motion.div>
                        </Box>
                    </Zoom>

                    {error && (
                        <Alert 
                            severity={needsVerification ? "warning" : "error"} 
                            sx={{ mb: 2 }}
                            action={
                                needsVerification && (
                                    <Button color="inherit" size="small" onClick={handleResendVerification}>
                                        Resend Email
                                    </Button>
                                )
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {!submitted ? (
                        <motion.div
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    fullWidth
                                    label="Email / Username / Phone"
                                    type="text"
                                    value={identifier}
                                    onChange={(e) => {
                                        setIdentifier(e.target.value);
                                        setError('');
                                        setNeedsVerification(false);
                                    }}
                                    error={!!error}
                                    margin="normal"
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon sx={{ color: theme.palette.primary.main }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.common.white, 0.7),
                                        }
                                    }}
                                />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    disabled={loading}
                                    startIcon={!loading && <Send />}
                                    sx={{
                                        py: 1.5,
                                        mb: 2,
                                        fontSize: '1rem',
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                                </Button>

                                <Button
                                    fullWidth
                                    variant="text"
                                    startIcon={<ArrowBack />}
                                    onClick={() => navigate('/login')}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Back to Login
                                </Button>
                            </Box>
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Check Your Email / Messages
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    We've sent a password reset link to <strong>{identifier}</strong>.<br />
                                    The link will expire in 60 minutes.
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate('/login')}
                                    sx={{
                                        py: 1.5,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                    }}
                                >
                                    Return to Login
                                </Button>
                            </Box>
                        </motion.div>
                    )}
                </StyledPaper>
            </Fade>
        </Box>
    );
};

export default InstitutionAdminForgotPassword;